import React from 'react';
import PropTypes from 'prop-types';
import { scroller } from 'react-scroll';
import { Helmet } from 'react-helmet';
import intersection from 'lodash/intersection';

import { Helpers } from '../../core/src/helpers';
import SEOLinks from '../../page/components/seo-links/seo-links';
import PDPDataProvider from './pdp-data-provider';
import PDPMainBlock from './pdp-main-block';
import PurchaseButtonContainer from '../../product/components/purchase-button-container';
import PurchaseButtonValidator from '../../product/components/validate-purchase-btn';
import ABTest from '../../abtest/components/abtest';
import { withError } from '../../shared/components/error-boundary';
import SMBSlider from '../../shared/components/smb-slider';
import AboveTheFoldOnlyServerRender from '../../shared/components/above-the-fold-only-server-render';
import PDPTabs from './tabs';
import BvScript from '../../bv/components/bv-script';
import PDPOosPopup from './pdp-oos-popup';
import PrivateStoreRedirect from '../../utility/components/private-store-redirect';
import Disclaimer from '../../page/components/disclaimer';
import MoxieConcierge from '../../page/components/moxie-concierge';
import ErrorRedirect from '../../main/components/error-redirect';
import { ModalProvider } from '../../context/modal/modal-context';
import { ValidationProvider } from '../../context/validation/validation-context';
import { ProductProvider } from '../../context/product/product-context';
import GenesysChat from './pdp-genesys-chat';
import DaasPopup from './pdp-daas-popup';
import CanadaPopup from './pdp-cn-redirect';

import '../css/pdp.less';
import '../css/pdp-mobile.less';

//import { withPropsChecker, stateChecker } from '../../test/debug';

const { memoize } = Helpers;
const TITLE = 'HP.com Official Store';
const TITLE_POSTFIX = ' | HP.com Official Store';
const DESCRIPTION =
    'Save with Free Shipping when you shop online with HP. Find all product features, specs, accessories, reviews and offers.';
const EMPTY_OBJECT = {};

class PDP extends React.Component {
    static whyDidYouRender = true;
    static propTypes = {
        canonical: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        titlePostfix: PropTypes.string,
        pageNotFound: PropTypes.bool,
        product: PropTypes.object.isRequired,
        seoParagraph: PropTypes.string,
        receivePDPInfo: PropTypes.func.isRequired,
        receiveVariantInfo: PropTypes.func.isRequired,
        fetchPDP: PropTypes.func.isRequired,
        clearPDPInfo: PropTypes.func.isRequired,
        history: PropTypes.object,
        location: PropTypes.object,
        match: PropTypes.object,
    };

    getDefaultState = pdpInfo => {
        const { product } = this.props;
        const { variants = [], prdClass } = pdpInfo || product;

        const { selectedColor, variant } = this.getDefaultColor(variants);
        return {
            //TODO: this should come from the page settings instead of hardcoding EXT type pdps to the 2nd tab
            tabIndex: prdClass === 'EXT' ? 1 : 0,
            addOns: null,
            selectedColor,
            variant,
            totalPrice: undefined,
        };
    };

    constructor(props) {
        super(props);
        this.handleTabChange = this.handleTabChange.bind(this);
        this.state = this.getDefaultState();
    }

    shouldComponentUpdate(nextProps, nextState) {
        /**
         * Only update when product is not default redux product,
         * or there was an error fetching a pdp.
         */
        return !!nextProps.product.sku || !!nextProps.product.error;
    }

    componentDidMount() {
        const { match } = this.props;
        const { slug, filters } = match.params;
        this.fetchProduct(slug, filters);
    }

    componentDidUpdate(prevProps, prevState) {
        const oldSlug = prevProps.match.params.slug;
        const { slug: newSlug, filters } = this.props.match.params;
        if (oldSlug !== newSlug) {
            this.fetchProduct(newSlug, filters);
        }
    }

    componentWillUnmount() {
        this.props.clearPDPInfo();
    }

    fetchProduct(slug, filters) {
        const { product } = this.props;

        if (decodeURIComponent(product.slug) !== decodeURIComponent(slug) || product.fromReduxCache) {
            //reset state when fetching a new product.
            //TODO: this is not great to to have to set the state before an after the API call
            //, but there are some weird race conditions here
            this.setState(this.getDefaultState({}));
            this.props.fetchPDP(slug, filters).then(pdp => {
                const { pdpInfo } = pdp;
                this.setState(this.getDefaultState(pdpInfo));
            });
        } else {
            this.props.trackPDPPageView(product);
        }
    }

    hasClientRedirect = () => {
        let { redirects } = this.props;
        let { destination } = redirects || {};
        return destination && destination.length > 0;
    };

    getColorGroup = memoize(
        (sku, fromReduxCache) => {
            const colorGroup = {
                displayName: 'Color',
                key: 'color',
                values: [],
            };
            const variants = this.props.product.variants || [];
            const colorVariants = variants.filter(variant => variant.attributes.type === 'color-hex');
            if (variants.length > 0) {
                colorGroup.values = variants.reduce((values, variant) => {
                    //for CTO if a color is disabled hide it completely
                    if (variant.attributes.type === 'color-hex' && !variant.disabled) {
                        const newVariant = Object.assign(
                            { disabled: false, flag: 'show', product: variant.product },
                            variant.attributes
                        );
                        values.push(newVariant);
                    }
                    return values;
                }, []);
            }
            return colorGroup;
        },
        (sku, fromReduxCache) => `${sku}-${fromReduxCache}`
    );

    changeColor = (selectedColor, colorGroups) => {
        const i = colorGroups.values.indexOf(selectedColor);
        const variant = selectedColor.product;
        this.setState({ selectedColor: i });
        //dont do anything if variant image array is empty
        if (selectedColor.product && selectedColor.product.images && selectedColor.product.images.length > 0) {
            this.modifyProductWithVariant({ ...selectedColor.product, selectedColor: selectedColor.displayName });
        }
    };

    getDefaultColor = variants => {
        let selectedColor = 0;
        let variant = null;
        if (variants.length > 0) {
            for (var i = variants.length - 1; i >= 0; i--) {
                if (variants[i].isDefault) {
                    selectedColor = i;
                    variant = variants[i].product;
                    break;
                }
            }
        }
        return { selectedColor, variant };
    };

    modifyProductWithVariant = variant => {
        this.setState({ variant });
        //safe check for PRT product
        if (variant.hasOwnProperty('sku')) {
            const { slug, prdClass } = this.props.product;
            //hacky way of tracking pageViews when variants are selected
            this.props.receiveVariantInfo({ ...variant, variantSelected: true, slug, prdClass });
        }
    };

    scrollToTabs(isTabSticky) {
        const scrollProps = {
            duration: 1000,
            smooth: true,
            offset: isTabSticky ? 0 : -50,
        };
        scroller.scrollTo('pdpTabs', scrollProps);
    }

    changeTabs(tab, scroll = true, isTabSticky) {
        if (this.state.tabIndex !== tab) {
            this.setState({ tabIndex: tab });
        }
        if (scroll) this.scrollToTabs(isTabSticky);
    }

    handleTabChange(tab, scroll, isTabSticky) {
        return this.changeTabs(tab, scroll, isTabSticky);
    }

    getModelId = product => {
        const { modelId } = product;
        let ids = modelId && modelId.constructor === Array ? modelId.slice(0) : [];
        let last = ids.pop();
        while (!last && ids.length) last = ids.pop();
        return last;
    };

    displayCarePackModule = (product, carePacks, hasBundle, isSmb) => {
        try {
            const { attributes = EMPTY_OBJECT, eol, categories = [], pdpConfig = EMPTY_OBJECT } = product;
            const { pdpCarepackModule = EMPTY_OBJECT } = pdpConfig;
            const isCarepack = !!attributes.carepacktype && attributes.carepacktype !== 'NA';
            const inCategory =
                intersection(['Wt_5b', 'Gxc6P', 'bXmjy', 'cGAFB', 'OwSKW', 'xm5DJ'], categories).length > 0;

            return (
                inCategory &&
                !hasBundle &&
                !eol &&
                ((isSmb && carePacks.length < 1 && !isCarepack) ||
                    (['STO', 'BTO', 'PRT', 'FIXEDBUNDLE'].indexOf(product.prdClass) > -1 && carePacks.length > 1) ||
                    pdpCarepackModule.displayCarePackModule)
            );
        } catch (e) {
            return false;
        }
    };

    getPurchaseButton = memoize(
        (product, CTOQuery, addOns, forceOOS, disableXsellPopup) => {
            return (
                <PurchaseButtonContainer
                    product={product}
                    CTOQuery={CTOQuery}
                    addOns={addOns}
                    forceOOS={forceOOS}
                    disableXsellPopup={disableXsellPopup}
                    templateKey="pdp"
                    enableCTOAddToCart={true}
                    enableAddToQuote={true}
                />
            );
        },
        (product, CTOQuery, addOns) => `${product.sku}+${addOns}+${!!product.bundle}+${product.fromReduxCache}`
    );

    updateSelectedAddOns = (newSelectedAddOns, totalPrice) => {
        const addOns = this.buildAddOns(newSelectedAddOns);
        this.setState({ addOns, totalPrice });
    };

    setVariantData = (product, variant) => {
        //TODO: the variant pdp config will always take over if set,
        // maybe we need a way to delete the page for child pdps

        //newly added 1/28, set variant's pdpconfig to be product.pdpConfig and variant.pdpConfig merged
        const { pdpConfig } = product;
        variant.pdpConfig = Object.assign({}, pdpConfig, variant.pdpConfig);
        // do not overwrite prdClass
        const { prdClass, ...variantData } = variant;
        return Object.assign({}, product, variantData);
    };

    buildAddOns = addOns => {
        if (!addOns) return null;
        return addOns.map(addOn => {
            const qty = (!!addOn.metadata && addOn.metadata.quantity) || 1;
            return Object.assign({}, { product: addOn, qty });
        });
    };

    render() {
        if (this.props.pageNotFound || this.hasClientRedirect()) {
            return <ErrorRedirect />;
        }

        let { product } = this.props;
        const { tabIndex, selectedColor, variant, addOns, totalPrice } = this.state;
        if (variant) {
            product = this.setVariantData(product, variant);
        }
        let { pdpConfig = EMPTY_OBJECT, seo = EMPTY_OBJECT } = product;
        const { copyBlock, wordCloud } = seo;

        let {
            templateKey = 'default',
            customCode = EMPTY_OBJECT,
            pdpDetails = EMPTY_OBJECT,
            moxieConcierge = EMPTY_OBJECT,
        } = pdpConfig;
        const { forceOOS, disableXsellPopup } = pdpDetails;
        const enableShippingBanner = Helpers.hasShippingOffer(product);
        const { attributes = EMPTY_OBJECT, eol } = product;
        const isCarepack = !!attributes.carepacktype && attributes.carepacktype !== 'NA';
        const isProductPC = Helpers.isProductPC(product) === true;
        const isSmb = product.isSmb === 'Yes';
        const showPaypalBanner = isProductPC && !isSmb;
        const { enableMoxie } = moxieConcierge;

        let CTOQuery = {};
        const colorGroup = this.getColorGroup(product.sku, product.fromReduxCache);
        const disableAlertBanner =
            eol ||
            attributes.pm_bigseries === 'HP Sprocket Photo Printer' ||
            !new RegExp('(?:printer|toner)', 'gi').test(attributes.pm_category)
                ? false
                : new RegExp('(?:printer|toner)', 'gi').test(attributes.pm_category);

        return (
            <ValidationProvider>
                <ModalProvider className={`pdp-bg clearfix${templateKey ? ` ${templateKey}` : ''}`}>
                    <Helmet>
                        <meta charSet="utf-8" />
                        <title>{this.props.title}</title>
                        <link rel="canonical" href={this.props.canonical} />
                        <meta name="description" content={this.props.description} />
                        <meta name="keywords" content={this.props.keywords} />
                    </Helmet>
                    {customCode.css ? <style dangerouslySetInnerHTML={Helpers.createMarkup(customCode.css)} /> : null}
                    <PrivateStoreRedirect
                        privateStoreOnly={product.privateStoreOnly}
                        privateStores={product.privateStores}
                    />
                    <div className="pdp-main-block clearfix" id="content" name="content">
                        <PDPDataProvider product={product} upsellLimit={4}>
                            {productWithAsyncData => {
                                const { xsell, bundle, oosBackup = {}, carePacks } = productWithAsyncData;
                                const PurchaseButton = this.getPurchaseButton(
                                    productWithAsyncData,
                                    CTOQuery,
                                    addOns,
                                    forceOOS,
                                    disableXsellPopup
                                );
                                const hasBundle = !!bundle;
                                const hasOosBackup =
                                    !!oosBackup.productsList &&
                                    !!oosBackup.productsList.length &&
                                    product.prdClass !== 'PRT';
                                const showCarepackModule = this.displayCarePackModule(
                                    product,
                                    carePacks,
                                    hasBundle,
                                    isSmb
                                );

                                const mainBlockProps = {
                                    getModelId: () => this.getModelId(product),
                                    handleTabChange: this.handleTabChange,
                                    modifyProductWithVariant: this.modifyProductWithVariant,
                                    updateSelectedAddOns: this.updateSelectedAddOns,
                                    selectedColor,
                                    changeColor: this.changeColor,
                                    product: productWithAsyncData,
                                    colorGroup,
                                    PurchaseButton,
                                    enableShippingBanner,
                                    showCarepackModule,
                                    carePacks,
                                    isSmb,
                                    disableAlertBanner,
                                    template: templateKey,
                                    totalPrice,
                                    hasBundle: hasBundle && !product.eol,
                                    showPaypalBanner,
                                };

                                return (
                                    <ProductProvider productContext={mainBlockProps}>
                                        {isSmb && <SMBSlider />}
                                        <PDPMainBlock />
                                        <div className="pdp-line"></div>
                                        <PDPTabs
                                            product={productWithAsyncData}
                                            tabIndex={tabIndex}
                                            handleTabChange={this.handleTabChange}
                                            purchaseButton={
                                                product.prdClass === 'PRT' ? (
                                                    <PurchaseButtonValidator
                                                        product={product}
                                                        purchaseButton={PurchaseButton}
                                                        withInventoryMsg={false}
                                                        className="nav-pb-validator"
                                                    />
                                                ) : (
                                                    PurchaseButton
                                                )
                                            }
                                        />
                                        <SEOLinks links={wordCloud} useDefaultLinks={true} />
                                        {hasOosBackup && (
                                            <PDPOosPopup
                                                backups={oosBackup.productsList}
                                                sku={product.sku}
                                                customFields={oosBackup.metaData}
                                                pdpDetails={pdpDetails}
                                            />
                                        )}
                                    </ProductProvider>
                                );
                            }}
                        </PDPDataProvider>
                        <ABTest experimentID="genesys">
                            {data =>
                                data &&
                                data.METRIC_VAR === 'var1' && (
                                    <GenesysChat
                                        METRIC_VAR={data.METRIC_VAR}
                                        METRIC_PREFIX={data.METRIC_PREFIX}
                                        key={
                                            this.props.match && this.props.match.params && this.props.match.params.slug
                                        }
                                    />
                                )
                            }
                        </ABTest>
                        <ABTest experimentID="daas">
                            {data =>
                                data &&
                                data.METRIC_VAR === 'var1' && (
                                    <DaasPopup
                                        descOne={data.Desc1}
                                        descTwo={data.Desc2}
                                        shopURL={data.ShopURL}
                                        METRIC_VAR={data.METRIC_VAR}
                                        METRIC_PREFIX={data.METRIC_PREFIX}
                                        mainImage={product.images}
                                    />
                                )
                            }
                        </ABTest>

                        <ABTest experimentID="canadapopup">
                            {data =>
                                data &&
                                data.METRIC_VAR === 'var1' && (
                                    <CanadaPopup
                                        urlProp={data.urlProp}
                                        METRIC_VAR={data.METRIC_VAR}
                                        METRIC_PREFIX={data.METRIC_PREFIX}
                                    />
                                )
                            }
                        </ABTest>
                    </div>
                    <AboveTheFoldOnlyServerRender renderOn={'idle'} skip={true} idleBuffer={1000}>
                        <BvScript sku={product.sku} />
                    </AboveTheFoldOnlyServerRender>
                    <Disclaimer seoParagraph={copyBlock} />
                    {enableMoxie && <MoxieConcierge />}
                </ModalProvider>
            </ValidationProvider>
        );
    }
}

PDP.defaultProps = {
    seoParagraph: '',
    title: TITLE,
    titlePostfix: TITLE_POSTFIX,
    description: DESCRIPTION,
};

export default withError(PDP);
